import { useMemo, useState } from 'react';
import geometry from './fixtures/pucEdmGeometry.json';

export interface NetworkGeometryPipe {
    id: string;
    /** Nominal diameter (mm) */
    dn: number;
    /** Length (m) */
    len: number;
    /** Mean pressure (bar) — optimisation result */
    p: number;
    /** Velocity (m/s) — optimisation result */
    v: number;
    c: [number, number][];
}
export interface NetworkGeometrySubstation {
    id: string;
    c: [number, number];
    /** Nominal power (kW) */
    kw: number;
    /** Delivered power (MW) — optimisation result */
    mw: number;
}
export interface NetworkGeometry {
    source: string;
    bbox: [number, number, number, number];
    pipes: NetworkGeometryPipe[];
    substations: NetworkGeometrySubstation[];
    departures: Array<{ name: string; c: [number, number] }>;
}

/** Real supply network of "PUC EDM - NemoOptimisation" (426 pipes, 127 substations, 4 production sites). */
export const PUC_EDM_GEOMETRY = geometry as unknown as NetworkGeometry;

export type NetworkMapColorBy = 'none' | 'pressure' | 'velocity' | 'diameter';

/* Data-viz palette (hex allowed for chart/map datasets): high = red, low = blue, as in Nemo carto results. */
const RAMP = ['#2c7bb6', '#00a6ca', '#00ccbc', '#90eb9d', '#ffff8c', '#f9d057', '#f29e2e', '#e76818', '#d7191c'];
const NEUTRAL_PIPE = '#E1352B';

function rampColor(t: number) {
    const clamped = Math.max(0, Math.min(1, t));
    return RAMP[Math.round(clamped * (RAMP.length - 1))];
}

const METRIC: Record<Exclude<NetworkMapColorBy, 'none'>, { label: string; unit: string; get: (p: NetworkGeometryPipe) => number }> = {
    pressure: { label: 'Pression moyenne', unit: 'bar', get: (p) => p.p },
    velocity: { label: 'Vitesse', unit: 'm/s', get: (p) => p.v },
    diameter: { label: 'DN', unit: 'mm', get: (p) => p.dn },
};

export interface Size2MixNetworkMapProps {
    geometry?: NetworkGeometry;
    colorBy?: NetworkMapColorBy;
    showSubstations?: boolean;
    showDepartures?: boolean;
    selectedId?: string | null;
    onSelect?: (id: string | null, kind: 'pipe' | 'substation' | 'departure' | null) => void;
    /** Optional entity name filter — matching pipes/substations are highlighted. */
    highlight?: string;
    height?: number | string;
}

/**
 * Lightweight SVG map of a network (no Mapbox token needed).
 * Use it for Topologie > Éditeur réseau and Optimisation > Résultats cartographiques.
 */
export function Size2MixNetworkMap({
    geometry: geo = PUC_EDM_GEOMETRY,
    colorBy = 'none',
    showSubstations = true,
    showDepartures = true,
    selectedId,
    onSelect,
    highlight,
    height = '100%',
}: Size2MixNetworkMapProps) {
    const [hover, setHover] = useState<string | null>(null);
    const [minLon, minLat, maxLon, maxLat] = geo.bbox;
    const kx = Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180));
    const W = 1000;
    const H = Math.round((W * (maxLat - minLat)) / ((maxLon - minLon) * kx));
    const pad = 20;
    const project = ([lon, lat]: [number, number]): [number, number] => [
        pad + ((lon - minLon) / (maxLon - minLon)) * (W - 2 * pad),
        pad + ((maxLat - lat) / (maxLat - minLat)) * (H - 2 * pad),
    ];

    const metric = colorBy === 'none' ? null : METRIC[colorBy];
    const range = useMemo(() => {
        if (!metric) return [0, 1] as const;
        const values = geo.pipes.map(metric.get);
        return [Math.min(...values), Math.max(...values)] as const;
    }, [geo, metric]);

    const paths = useMemo(
        () =>
            geo.pipes.map((p) => ({
                pipe: p,
                d: p.c.map((pt, i) => `${i ? 'L' : 'M'}${project(pt).map((n) => n.toFixed(1)).join(',')}`).join(''),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [geo],
    );

    const q = highlight?.trim().toLowerCase();
    const hoveredPipe = hover ? geo.pipes.find((p) => p.id === hover) : undefined;
    const hoveredSub = hover ? geo.substations.find((s) => s.id === hover) : undefined;

    return (
        <div style={{ position: 'relative', height, background: 'var(--nj-core-color-grey-100)', overflow: 'hidden' }}>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', display: 'block' }} onClick={() => onSelect?.(null, null)}>
                {paths.map(({ pipe, d }, i) => {
                    const isSel = selectedId === pipe.id;
                    const isHit = q ? pipe.id.toLowerCase().includes(q) : false;
                    const color = metric ? rampColor((metric.get(pipe) - range[0]) / (range[1] - range[0] || 1)) : NEUTRAL_PIPE;
                    return (
                        <path
                            key={`${pipe.id}-${i}`}
                            d={d}
                            fill="none"
                            stroke={isSel || isHit ? '#0b74c0' : color}
                            strokeWidth={(isSel || isHit ? 3 : 1) + pipe.dn / 120}
                            strokeLinecap="round"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHover(pipe.id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect?.(pipe.id, 'pipe');
                            }}
                        />
                    );
                })}
                {showSubstations &&
                    geo.substations.map((s) => {
                        const [x, y] = project(s.c);
                        const isSel = selectedId === s.id || (q ? s.id.toLowerCase().includes(q) : false);
                        return (
                            <circle
                                key={s.id}
                                cx={x}
                                cy={y}
                                r={isSel ? 7 : 4}
                                fill={isSel ? '#0b74c0' : '#ffffff'}
                                stroke="#333333"
                                strokeWidth={1.2}
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHover(s.id)}
                                onMouseLeave={() => setHover(null)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect?.(s.id, 'substation');
                                }}
                            />
                        );
                    })}
                {showDepartures &&
                    geo.departures.map((dep) => {
                        const [x, y] = project(dep.c);
                        return (
                            <g
                                key={dep.name}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect?.(dep.name, 'departure');
                                }}
                            >
                                <rect x={x - 9} y={y - 9} width={18} height={18} rx={3} fill="#1F1F1F" stroke={selectedId === dep.name ? '#0b74c0' : '#ffffff'} strokeWidth={2} />
                                <text x={x + 14} y={y + 5} fontSize={15} fontWeight={700} fill="#1F1F1F" stroke="#ffffff" strokeWidth={3} paintOrder="stroke">
                                    {dep.name}
                                </text>
                            </g>
                        );
                    })}
            </svg>

            {(hoveredPipe || hoveredSub) && (
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        padding: '8px 12px',
                        fontSize: 12,
                        background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                        border: '1px solid var(--nj-core-color-grey-300)',
                        borderRadius: 4,
                        pointerEvents: 'none',
                    }}
                >
                    {hoveredPipe && (
                        <>
                            <strong>Canalisation {hoveredPipe.id}</strong>
                            <div>
                                DN {hoveredPipe.dn} · {hoveredPipe.len} m · {hoveredPipe.p} bar · {hoveredPipe.v} m/s
                            </div>
                        </>
                    )}
                    {hoveredSub && (
                        <>
                            <strong>{hoveredSub.id}</strong>
                            <div>
                                {hoveredSub.kw} kW nominal · {hoveredSub.mw} MW livrés
                            </div>
                        </>
                    )}
                </div>
            )}

            {metric && <Size2MixMapLegend title={`Canalisations ${metric.label} [${metric.unit}]`} min={range[0]} max={range[1]} />}
        </div>
    );
}

export function Size2MixMapLegend({ title, min, max }: { title: string; min: number; max: number }) {
    return (
        <div
            style={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                padding: '8px 12px',
                fontSize: 12,
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: 4,
                display: 'flex',
                gap: 8,
            }}
        >
            <div style={{ width: 12, height: 120, background: `linear-gradient(to bottom, ${[...RAMP].reverse().join(',')})` }} />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <strong style={{ maxWidth: 120 }}>{title}</strong>
                <span>{max.toFixed(2)}</span>
                <span>{min.toFixed(2)}</span>
            </div>
        </div>
    );
}
